import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Edit3, Save, ChevronDown, ChevronRight, Lock, Users, Sparkles, Power, AlertCircle,
  Eye, Plus, Trash2, X, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';

const TAG_COLORS = {
  blue:   { dot: 'bg-blue-500',    chip: 'bg-blue-100 text-blue-800 border-blue-300',         ring: 'ring-blue-300' },
  green:  { dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-800 border-emerald-300', ring: 'ring-emerald-300' },
  red:    { dot: 'bg-red-500',     chip: 'bg-red-100 text-red-800 border-red-300',             ring: 'ring-red-300' },
  yellow: { dot: 'bg-yellow-500',  chip: 'bg-yellow-100 text-yellow-800 border-yellow-300',     ring: 'ring-yellow-300' },
  purple: { dot: 'bg-purple-500',  chip: 'bg-purple-100 text-purple-800 border-purple-300',     ring: 'ring-purple-300' },
  pink:   { dot: 'bg-pink-500',    chip: 'bg-pink-100 text-pink-800 border-pink-300',           ring: 'ring-pink-300' },
  gray:   { dot: 'bg-slate-500',   chip: 'bg-slate-100 text-slate-800 border-slate-300',        ring: 'ring-slate-300' },
  orange: { dot: 'bg-orange-500',  chip: 'bg-orange-100 text-orange-800 border-orange-300',     ring: 'ring-orange-300' },
  indigo: { dot: 'bg-indigo-500',  chip: 'bg-indigo-100 text-indigo-800 border-indigo-300',     ring: 'ring-indigo-300' },
  teal:   { dot: 'bg-teal-500',    chip: 'bg-teal-100 text-teal-800 border-teal-300',           ring: 'ring-teal-300' },
};
const CORES = Object.keys(TAG_COLORS);

const VARIAVEIS = [
  { v: '{nome}',       d: 'Nome do cliente' },
  { v: '{veiculo}',    d: 'Modelo do veículo' },
  { v: '{placa}',      d: 'Placa' },
  { v: '{valor}',      d: 'Valor da fatura' },
  { v: '{vencimento}', d: 'Data de vencimento' },
  { v: '{dias}',       d: 'Dias de atraso' },
  { v: '{tag}',        d: 'Nome da tag do cliente' },
];

const REGRA_LABEL_BY_TIPO = {
  vencendo_hoje:  'Vencendo hoje',
  atraso_1:       '1 dia de atraso',
  atraso_2:       '2 dias de atraso',
  atraso_3:       '3 dias de atraso',
  atraso_4_mais:  '+3 dias de atraso',
};

// ─────────────────────────────────────────────────────────────────────────────
export default function MensagensPanel({ API }) {
  const [tags, setTags]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [tagEditor, setTagEditor]     = useState(null);
  const [regraEditor, setRegraEditor] = useState(null);
  const [confirmDel, setConfirmDel]   = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/client-tags`, { withCredentials: true });
      const docs = r.data || [];
      setTags(docs);
      setExpanded((cur) => {
        if (Object.keys(cur).length > 0) return cur;
        const padrao = docs.find((t) => t.is_default);
        return padrao ? { [padrao._id]: true } : {};
      });
    } catch {
      toast.error('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const apagarTag = async (tag) => {
    try {
      await axios.delete(`${API}/client-tags/${tag._id}`, { withCredentials: true });
      toast.success(`Tag "${tag.nome}" removida`);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao remover tag');
    } finally {
      setConfirmDel(null);
    }
  };

  const toggleAtivo = async (tag, regra) => {
    try {
      await axios.put(`${API}/client-tags/${tag._id}/regras/${regra.regra_id}`,
        { ativo: !regra.ativo },
        { withCredentials: true });
      carregar();
    } catch {
      toast.error('Erro ao atualizar regra');
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Carregando mensagens...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-600" />
            Réguas de Cobrança por Tag
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Cada tag tem 5 regras fixas: <strong>Vencendo hoje · 1 dia · 2 dias · 3 dias · +3 dias</strong>.
            Ative as que quiser usar e edite as mensagens. Clientes sem tag específica recebem a régua <strong>PADRÃO</strong>.
          </p>
        </div>
        <Button onClick={() => setTagEditor({ mode: 'novo' })} className="gap-2" data-testid="btn-nova-tag">
          <Plus className="w-4 h-4" /> Nova Tag
        </Button>
      </div>

      <div className="space-y-3">
        {tags.map((tag) => (
          <TagReguaCard
            key={tag._id}
            tag={tag}
            expanded={!!expanded[tag._id]}
            onToggle={() => toggle(tag._id)}
            onEditarTag={() => setTagEditor({ mode: 'editar', tag })}
            onApagarTag={() => setConfirmDel({ tipo: 'tag', payload: tag })}
            onEditarRegra={(regra) => setRegraEditor({ tag, regra })}
            onToggleAtivo={(regra) => toggleAtivo(tag, regra)}
          />
        ))}
      </div>

      <Card className="p-4 bg-slate-50 border-dashed">
        <p className="text-sm font-semibold text-slate-700 mb-2">💡 Como funciona</p>
        <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
          <li>Quando uma fatura vence, o sistema verifica a <strong>tag do cliente</strong>.</li>
          <li>Se ele tem tag (ex: VIP), usa a régua dessa tag conforme os dias de atraso.</li>
          <li>Se a regra dessa tag está <strong>inativa</strong> ou ele <strong>não tem tag</strong>, cai na régua <strong>PADRÃO</strong>.</li>
          <li><strong>Apenas 1 mensagem por cliente por dia</strong> (sem cooldown).</li>
          <li>Variáveis disponíveis: {VARIAVEIS.map((v) => <code key={v.v} className="mx-0.5 px-1 bg-white rounded text-violet-600">{v.v}</code>)}</li>
        </ul>
      </Card>

      {tagEditor && <TagModal API={API} editor={tagEditor} onClose={() => setTagEditor(null)} onSaved={carregar} />}
      {regraEditor && <RegraModal API={API} editor={regraEditor} onClose={() => setRegraEditor(null)} onSaved={carregar} />}
      {confirmDel && (
        <ConfirmModal
          payload={confirmDel}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => apagarTag(confirmDel.payload)}
        />
      )}
    </div>
  );
}

// ─── Card da régua de uma tag ────────────────────────────────────────────────
function TagReguaCard({ tag, expanded, onToggle, onEditarTag, onApagarTag, onEditarRegra, onToggleAtivo }) {
  const cor = TAG_COLORS[tag.cor] || TAG_COLORS.blue;
  const regras = tag.regras || [];
  const ativasCount = regras.filter((r) => r.ativo).length;

  return (
    <Card className={`overflow-hidden border-l-4 ${cor.ring} ring-1`} data-testid={`tag-card-${tag.nome}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition cursor-pointer"
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className={`w-3 h-3 rounded-full ${cor.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900">{tag.nome}</span>
            {tag.is_default && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> default
              </span>
            )}
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <Users className="w-3 h-3" /> {tag.total_clientes} cliente{tag.total_clientes === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-xs text-emerald-700 font-medium">{ativasCount}/5 regras ativas</span>
          </div>
          {tag.descricao && <p className="text-xs text-slate-500 truncate">{tag.descricao}</p>}
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={onEditarTag} data-testid={`btn-editar-tag-${tag.nome}`}>
            <Edit3 className="w-4 h-4" />
          </Button>
          {!tag.is_default && (
            <Button variant="ghost" size="sm" onClick={onApagarTag} className="text-red-600 hover:bg-red-50" data-testid={`btn-apagar-tag-${tag.nome}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t bg-slate-50/40 p-4 space-y-2">
          {regras.map((regra) => (
            <RegraRow
              key={regra.regra_id}
              regra={regra}
              onEdit={() => onEditarRegra(regra)}
              onToggleAtivo={() => onToggleAtivo(regra)}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function RegraRow({ regra, onEdit, onToggleAtivo }) {
  return (
    <div className={`bg-white border rounded-lg p-3 flex items-start gap-3 ${regra.ativo ? '' : 'opacity-60'}`}
         data-testid={`regra-row-${regra.regra_id}`}>
      <button
        type="button"
        onClick={onToggleAtivo}
        className={`mt-1 w-9 h-5 rounded-full p-0.5 transition shrink-0 ${regra.ativo ? 'bg-emerald-500' : 'bg-slate-300'}`}
        title={regra.ativo ? 'Ativa' : 'Inativa'}
        aria-label={regra.ativo ? 'Desativar' : 'Ativar'}
      >
        <span className={`block w-4 h-4 bg-white rounded-full transition ${regra.ativo ? 'translate-x-4' : ''}`} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-900">{regra.label}</span>
          {regra.horario_override && (
            <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded font-mono inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {regra.horario_override}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{regra.mensagem || <em className="text-slate-400">Sem mensagem</em>}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onEdit}><Edit3 className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

// ─── Modal de Tag ────────────────────────────────────────────────────────────
function TagModal({ API, editor, onClose, onSaved }) {
  const isEdit = editor.mode === 'editar';
  const tag = editor.tag || {};
  const [nome, setNome]           = useState(tag.nome || '');
  const [cor, setCor]             = useState(tag.cor || 'blue');
  const [descricao, setDescricao] = useState(tag.descricao || '');
  const [saving, setSaving]       = useState(false);
  const podeEditarNome = !tag.is_default;

  const salvar = async () => {
    if (!nome.trim()) { toast.error('Nome obrigatório'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/client-tags/${tag._id}`,
          { nome: nome.trim(), cor, descricao },
          { withCredentials: true });
      } else {
        await axios.post(`${API}/client-tags`,
          { nome: nome.trim(), cor, descricao },
          { withCredentials: true });
      }
      toast.success(isEdit ? 'Tag atualizada' : 'Tag criada (5 regras prontas para configurar)');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="modal-tag">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Editar Tag — ${tag.nome}` : 'Nova Tag'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Edite o nome, cor e descrição.' : 'Crie uma tag para agrupar clientes. Ela já vem com 5 regras prontas para você ativar e personalizar.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700">Nome</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              disabled={!podeEditarNome}
              placeholder="Ex: VIP, Acordo, Funcionário"
              className="w-full mt-1 px-3 py-2 border rounded-lg disabled:bg-slate-100 disabled:text-slate-500"
              data-testid="input-tag-nome"
            />
            {!podeEditarNome && <p className="text-[10px] text-slate-500 mt-1">A tag PADRÃO não pode ser renomeada.</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Cor</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {CORES.map((c) => {
                const cfg = TAG_COLORS[c];
                return (
                  <button key={c} type="button" onClick={() => setCor(c)}
                    className={`w-8 h-8 rounded-full ${cfg.dot} ${cor === c ? 'ring-2 ring-offset-2 ring-slate-900' : ''}`}
                    aria-label={c} />
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Descrição (opcional)</label>
            <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Clientes prioritários"
              className="w-full mt-1 px-3 py-2 border rounded-lg" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="gap-2" data-testid="btn-salvar-tag">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal de Regra (editar mensagem + horário override) ─────────────────────
function RegraModal({ API, editor, onClose, onSaved }) {
  const tag = editor.tag;
  const r = editor.regra || {};
  const [mensagem, setMensagem]                 = useState(r.mensagem || '');
  const [horarioOverride, setHorarioOverride]   = useState(r.horario_override || '');
  const [ativo, setAtivo]                       = useState(r.ativo ?? true);
  const [saving, setSaving]                     = useState(false);
  const [preview, setPreview]                   = useState(null);
  const [previewLoading, setPreviewLoading]     = useState(false);

  const insertVar = (v) => setMensagem((m) => m + v);

  // Preview com debounce
  useEffect(() => {
    if (!mensagem.trim()) { setPreview(null); return; }
    const id = setTimeout(async () => {
      setPreviewLoading(true);
      const dias_map = { vencendo_hoje: 0, atraso_1: 1, atraso_2: 2, atraso_3: 3, atraso_4_mais: 5 };
      try {
        const res = await axios.post(`${API}/notificacoes/preview-mensagem`,
          { mensagem, tag_nome: tag.nome, dias_atraso: dias_map[r.tipo] ?? 0 },
          { withCredentials: true });
        setPreview(res.data);
      } catch { setPreview(null); }
      finally { setPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(id);
  }, [mensagem, tag.nome, r.tipo, API]);

  const salvar = async () => {
    if (!mensagem.trim()) { toast.error('Mensagem obrigatória'); return; }
    if (horarioOverride && !/^\d{2}:\d{2}$/.test(horarioOverride)) {
      toast.error('Horário deve estar no formato HH:MM (ex: 08:35)');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/client-tags/${tag._id}/regras/${r.regra_id}`, {
        ativo,
        mensagem,
        horario_override: horarioOverride || null,
      }, { withCredentials: true });
      toast.success('Regra atualizada');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-regra">
        <DialogHeader>
          <DialogTitle>
            {REGRA_LABEL_BY_TIPO[r.tipo] || r.label} — Tag <span className="text-violet-600">{tag.nome}</span>
          </DialogTitle>
          <DialogDescription>
            Edite a mensagem que será enviada nesta faixa. Você também pode definir um horário específico (substitui o global).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAtivo((v) => !v)}
              className={`px-3 py-2 rounded-lg border-2 font-semibold text-sm transition ${
                ativo ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-100 text-slate-500'
              }`}
              data-testid="btn-toggle-ativo"
            >
              <Power className="w-4 h-4 inline mr-1" /> {ativo ? 'Regra ATIVA' : 'Regra INATIVA'}
            </button>
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Horário específico (opcional)
              </label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="text"
                  value={horarioOverride}
                  onChange={(e) => setHorarioOverride(e.target.value)}
                  placeholder="ex: 08:35 (vazio = global)"
                  className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                  data-testid="input-horario-override"
                />
                {horarioOverride && (
                  <Button variant="ghost" size="sm" onClick={() => setHorarioOverride('')}><X className="w-4 h-4" /></Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-700">Mensagem</label>
              <div className="flex gap-1 flex-wrap">
                {VARIAVEIS.map((v) => (
                  <button key={v.v} type="button" onClick={() => insertVar(v.v)}
                    className="text-[10px] px-1.5 py-0.5 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded font-mono"
                    title={v.d}>{v.v}</button>
                ))}
              </div>
            </div>
            <textarea
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
              placeholder="Olá {nome}..."
              data-testid="textarea-mensagem"
            />
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-semibold text-slate-700">Pré-visualização</span>
              {preview?.exemplo_de === 'cliente_real' && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium">cliente real + fatura real</span>}
              {preview?.exemplo_de === 'cliente_real_sem_fatura' && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">cliente real, fatura fictícia</span>}
              {preview?.exemplo_de === 'ficticio' && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium">100% fictício</span>}
              {previewLoading && <span className="text-[10px] text-slate-400 italic">atualizando...</span>}
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 min-h-[100px]" data-testid="preview-box">
              {preview?.preview ? (
                <>
                  <div className="text-[10px] text-emerald-700 mb-1.5 font-medium">
                    📱 Como aparecerá no WhatsApp de <strong>{preview.cliente_nome}</strong>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">{preview.preview}</pre>
                </>
              ) : (
                <p className="text-xs text-slate-400 italic text-center py-4">
                  {mensagem.trim() ? 'Carregando preview...' : 'Digite uma mensagem para ver a pré-visualização'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="gap-2" data-testid="btn-salvar-regra">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmModal({ payload, onCancel, onConfirm }) {
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600"><AlertCircle className="w-5 h-5" /> Remover tag "{payload.payload.nome}"?</DialogTitle>
          <DialogDescription>
            Os clientes que usavam esta tag voltarão a receber a régua PADRÃO. As 5 regras dessa tag serão apagadas.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Remover</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
